import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Card,
  Flex,
  CheckboxField,
  TextField,
  useTheme,
} from "@aws-amplify/ui-react";
import { useEffect, useId, useState } from "react";

type ToggleCardProps = {
  name: string;
  label: string;
  enabled: boolean;
  hasDates: boolean;
  startDate?: Date;
  endDate?: Date;
  description: string;
  isRequired?: boolean;
  messageLabel?: string;
  messagePlaceholder?: string;
  messageDefault?: string;
  onChange?: (enabled: boolean, message: string) => void;
  handleStartDate?: (date: Date, type: string) => void;
  handleEndDate?: (date: Date, type: string) => void;
};

export const ToggleCard = ({
  name,
  label,
  enabled,
  hasDates,
  startDate,
  endDate,
  description,
  isRequired = true,
  messageLabel = "Message when disabled",
  messagePlaceholder = "Enter message...",
  messageDefault = "",
  onChange,
  handleStartDate,
  handleEndDate,
}: ToggleCardProps) => {
  const [message, setMessage] = useState(messageDefault);
  const { tokens } = useTheme();
  const msgId = useId();

  useEffect(() => {
    setMessage(messageDefault);
  }, [messageDefault]);

  const handleSelectedEndDate = () => {
    if (startDate > endDate) {
      return startDate;
    } else {
      return endDate;
    }
  };

  const ring = enabled
    ? `0 0 0 1px ${tokens.colors.brand.primary[10].value}`
    : "0 0 0 0 transparent";

  return (
    <Card
      variation="outlined"
      style={{
        borderRadius: tokens.radii.medium.value,
        boxShadow: ring,
        transition: "box-shadow .15s ease, border-color .15s ease",
        marginBottom: "1.5rem",
      }}
    >
      <Flex direction="column" gap="0.75rem">
        <CheckboxField
          name={name}
          label={label}
          descriptiveText={description}
          checked={enabled}
          size="large"
          className="checkbox"
          onChange={(e) => {
            const check = (e.target as HTMLInputElement).checked;
            onChange?.(check, message);
          }}
        />
        <TextField
          id={msgId}
          label={messageLabel}
          placeholder={messagePlaceholder}
          value={message}
          onChange={(e) => {
            const val = (e.target as HTMLInputElement).value;
            setMessage(val);
            onChange?.(enabled, val);
          }}
          isDisabled={!enabled}
          autoComplete="off"
          direction="column"
          hasError={false}
          isRequired={isRequired}
          inputMode="text"
          wrap="nowrap"
          isReadOnly={false}
        />
        {hasDates && enabled && (
          <div>
            <div className="formItem field-row">
              <div className="date-input-label">
                Start Date <span className="required">*</span>
              </div>
              <div className="field-control">
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date, type: string) =>
                    handleStartDate(date, type)
                  }
                  minDate={new Date()}
                  showTimeInput
                  showMonthDropdown
                  showYearDropdown
                  placeholderText="Select start date"
                  timeInputLabel="Time:"
                  dateFormat="dd/MM/yyyy h:mm aa"
                  className="date-input"
                />
              </div>
            </div>

            <div className="formItem field-row">
              <div className="date-input-label">
                End Date <span className="required">*</span>
              </div>
              <div className="field-control">
                <DatePicker
                  selected={handleSelectedEndDate()}
                  onChange={(date: Date, type: string) =>
                    handleEndDate(date, type)
                  }
                  minDate={startDate}
                  showTimeInput
                  showMonthDropdown
                  showYearDropdown
                  placeholderText="Select end date"
                  timeInputLabel="Time:"
                  dateFormat="dd/MM/yyyy h:mm aa"
                  className="date-input"
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
    </Card>
  );
};
